/* prettier-ignore */

import { DestroyRef, Injectable, effect, inject, signal } from '@angular/core';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { parseDates, areDatesValid } from './chat-actions/helpers/date.utils';
import { getCoordinates } from './chat-actions/helpers/google-location.utils';
import { GenerateRouteService } from './chat-actions/generate-route.service';
import { RegisterFormService } from './chat-actions/register-form.service';
import { ShowItemDetailsService } from './chat-actions/show-item-details.service';
import { ShowComponentService } from './chat-actions/show-component.service';
import { RemoveItemFromCustomRouteService } from './chat-actions/remove-item-from-custom-route.service';
import { AddItemToCustomRouteService } from './chat-actions/add-item-to-custom-route.service';
import { SendCurrentRouteService } from './chat-actions/send-current-route.service';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { isNotSuccess, validateRoute } from './chat-actions/guards/custom-route.guard';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { GetFreeSlotsService } from './chat-actions/get-free-slots.service';
import { from, mapTo, Observable, of, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })

export class ChatActionHandlerService {
    private _sessionStorageService            = inject(SessionStorageService);
    private _destroyRef                       = inject(DestroyRef);
    private _generateRouteService             = inject(GenerateRouteService);
    private _registerFormService              = inject(RegisterFormService);
    private _showItemDetails                  = inject(ShowItemDetailsService);
    private _showComponentService             = inject(ShowComponentService);
    private _removeItemFromCustomRouteService = inject(RemoveItemFromCustomRouteService);
    private _addItemToCustomRouteService      = inject(AddItemToCustomRouteService);
    private _sendCurrentRouteService          = inject(SendCurrentRouteService)
    private _customRouteService               = inject(CustomRouteService);
    private _plannerDayStateService           = inject(PlannerDayStateService);
    private _getFreeSlotsService              = inject(GetFreeSlotsService)

    public openAIFeedback = signal(null);
    private generatingRoute = signal(false);

    private detectFormChanges = effect(() => {
        const form = this._registerFormService.signalForm();
        if (!this._registerFormService.isValidForm()) {
            const invalids = this._registerFormService.getInvalidControls();
            console.warn('TOOLS: Form invalid:', invalids);
            return;
        }

        if (this.generatingRoute())
            return;

        console.log("TOOLS:: SENDING ROUTE");
        this.generatingRoute.set(true);
        this.startRoute();
    }, { allowSignalWrites: true })

    public actionFromChatBot(result): Observable<void> {
        switch(result?.action){            
            case 'create_travel_dates':          return this.setTravelDates(result);         
            case 'create_travel_group_type':     return this.setTravelGroupType(result);     
            case 'create_travel_interests':      return this.setTravelInterests(result);     
            case 'create_transport_preferences': return this.setTransportPreference(result); 
            case 'create_distance_preferences':  return this.setDistancePreference(result);  
            case 'request_slot_candidates':      return this.showTimeLineOptions(result);    
            case 'show_timeline_options':        return this.showTimeLineOptions(result);    
            case 'show_point_details':           return this.showItemDetails(result);        
            case 'add_point_to_itinerary':       return this.addItem(result);                
            case 'remove_point_from_itinerary':  return this.removeItem(result);             
            case 'change_language':              return this.changeLanguage(result);         
            case 'return_to_timeline':           return this.showTimeLineView(result);       
            case 'return_to_map':                return this.showMapView(result);            
            case 'go_to_mail_form':              return this.showSendRouteView(result);      
            case 'get_current_itinerary':        return this.sendCurrentRoute(result);       
            case 'show_route_day':               return this.showRouteDay(result);           
            case 'create_starting_point':        return this.createStartingPoint(result);
            case 'modify_timeline_slot':         return this.modifyTimelineSlot(result);    
            case 'check_valid_form':             return this.checkValidForm(result);      
            default: return of(void 0);
        }
    }

    private generateInitialPayload(result: any) {
        return {
            action:         result.action,
            content:        null,
            is_successful:  false,
            operation_id:   result.operation_id,
            completion_id:  result.completion_id,
        }
    }

    private changeLanguage(item): Observable<void> {
        console.warn("change_language tool not implemented");
        const feedBack = this.generateInitialPayload(item);
        this.openAIFeedback.set(feedBack);
        return of(void 0);
    }

//---------- IA REGISTER USER TO GENERATE ROUTE ----------------------------------------
    private setTravelDates(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const dates = parseDates(item.start_date, item.end_date);
        if (!areDatesValid(dates)) {
            this.openAIFeedback.set({ ...feedBack, reason: 'invalid dates' });
            return of(void 0);
        }

        this._registerFormService.setDateRange(item);
        this.openAIFeedback.set({ ...feedBack, is_successful: true });
        return of(void 0);
    }

    private setTravelGroupType(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setTripType(item.group_id);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTravelInterests(item):  Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setInterestIdList(item.interest_ids)
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTransportPreference(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setTransportMeans(item.transport_methods);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setDistancePreference(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setDistancePreferences(item.max_distance_meters);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private createStartingPoint(args: any): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return from(getCoordinates(args?.starting_point)).pipe(
            tap(startLocation => {
                if (!startLocation) {
                    this.openAIFeedback.set({
                        ...feedBack,
                        is_successful: false,
                        reason: 'No se ha podido establecer el punto de partida'
                    });
                    return;
                }
                const address = { 
                    ...startLocation, 
                    title: args?.starting_point 
                };
                const res = this._registerFormService.setStartLocation(address);
                this.openAIFeedback.set({ ...feedBack, ...res });
            }),
            take(1),
            mapTo(void 0)
        );
    }

    private startRoute(): void {
        const values = this._registerFormService.getFormValues();
        this._generateRouteService.sendData(values, this._destroyRef, '/custom-route')
            .subscribe({
                next: (result) => {
                    this._registerFormService.resetForm();
                    //TODO: check this
                    this.generatingRoute.set(false);
                    console.log("Starting Route & Reseting Form");
                },
                error: err => {
                    console.error('TOOLS: Error registering user:', err);
                    this._sessionStorageService.removeAll();
                }
            });
    }
//---------- IA CHATBOT CUSTOM ROUTE FUNCTIONS ----------------------------------------
    private showItemDetails(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        return this._showItemDetails.show(item).pipe(
            take(1), // garantiza completar
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private addItem(args: any): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return this._addItemToCustomRouteService.addItem(args).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private removeItem(args): Observable<void>{
        const feedBack = this.generateInitialPayload(args);
        return this._removeItemFromCustomRouteService.removeItem(args).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private showTimeLineView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showTimelineComponent();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showMapView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showMapComponent();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showSendRouteView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showSendRouteComponent();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showTimeLineOptions(args):  Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return this._getFreeSlotsService.getAvailableItemsOnSlot(args?.day - 1, args?.hour).pipe(
            take(1),
            tap((res: any) => {
                if (!res?.is_successful){
                    this.openAIFeedback.set({...feedBack, res});
                    return;
                }

                let parsedResponse = JSON.parse(res?.content)
                this.openAIFeedback.set({ 
                    ...feedBack, 
                    content: parsedResponse,
                    'is_successful': true, 
                }) 
            }),
            mapTo(void 0)
        );
    }

    private sendCurrentRoute(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        const res = this._sendCurrentRouteService.getRoute();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showRouteDay(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        const route = this._customRouteService.customRouteData();
        const token = this._customRouteService.token();

        const ctx = validateRoute(route, token, args?.day);
        if (isNotSuccess(ctx)){
            this.openAIFeedback.set({...feedBack, ctx});
            return of(void 0);    
        }

        const dayIndex = Number(args?.day - 1);
        this._plannerDayStateService.setDayToShowIndex(dayIndex);
        this._showComponentService.showTimelineComponent();
        this.openAIFeedback.set({...feedBack, is_successful: true});
        return of(void 0);
    }

    private modifyTimelineSlot(args){
        const feedBack = this.generateInitialPayload(args);
        this.openAIFeedback.set({ ...feedBack, reason: 'Not implemented' });
        return of(void 0);
    }

    private checkValidForm(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        if (!this._registerFormService.isValidForm()) {
            const invalids = this._registerFormService.getInvalidControls()?.map(e => e.controlName)?.join(',');
            const res = {
                reason: `Se necesitan los siguientes inputs: ${invalids}`
            };
            this.openAIFeedback.set({...feedBack, ...res});
            return of(void 0);
        }

        const res = { is_successful: true };
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

}